import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { execute } from '@/graphql/execute';
import { decodeClearanceSection, type ClearanceSection } from '@/lib/clearance/sectionModel';
import { CLEARANCE_SCHEMA_VERSION, type ClearanceJson, type ClearanceLayerMeta } from '@/lib/clearance/types';
import { batchByproductsQuery } from '@/queries/byproduct.query';

export interface ClearanceFetchProgress {
  loadedBytes: number;
  totalBytes: number | null;
  phase: 'fetching' | 'decoding';
}

/**
 * Resolve a registered byproduct FILENAME to its signed download URL through
 * the batch query. Clearance metadata only ever carries registered filenames,
 * never S3 keys, so there is nothing to derive a basename from — the match is
 * exact. A failed batch query, an in-flight one, and "the batch resolved but
 * no byproduct with this filename exists" are three different situations the
 * dock renders differently, so they are not collapsed into one silent null.
 */
export const useClearanceByproductUrl = (batchId: string | undefined, filename: string | null | undefined) => {
  const query = useQuery({
    queryKey: ['batchByproducts', batchId],
    queryFn: () => execute(batchByproductsQuery, { id: batchId! }),
    enabled: !!batchId && !!filename,
    staleTime: 5 * 60_000,
  });
  if (!filename) return { url: null as string | null, isLoading: false, error: null as Error | null };
  if (query.error) return { url: null, isLoading: false, error: query.error as Error };
  if (query.isLoading || query.isFetching) return { url: null, isLoading: true, error: null };
  if (!query.data) return { url: null, isLoading: false, error: null };
  const match = query.data.batch?.byproducts?.find((b) => b.filename === filename);
  if (!match) return { url: null, isLoading: false, error: new Error(`No byproduct registered for "${filename}"`) };
  return { url: match.downloadUrl ?? null, isLoading: false, error: null };
};

async function fetchWithProgress(url: string, onProgress: (p: ClearanceFetchProgress) => void): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const total = Number(res.headers.get('content-length')) || null;
  if (!res.body) {
    onProgress({ loadedBytes: 0, totalBytes: total, phase: 'decoding' });
    return await res.arrayBuffer();
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress({ loadedBytes: loaded, totalBytes: total, phase: 'fetching' });
  }
  onProgress({ loadedBytes: loaded, totalBytes: total, phase: 'decoding' });
  const out = new Uint8Array(loaded);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.byteLength;
  }
  return out.buffer;
}

export interface UseClearanceResult {
  data: ClearanceJson | null;
  isLoading: boolean;
  error: Error | null;
  progress: ClearanceFetchProgress | null;
  refetch: () => void;
}

/**
 * Fetch station_clearance.json for an analysed layer. Keyed by the byproduct
 * filename rather than the layer: the object is immutable, so the result is
 * safe to keep for the whole session (`staleTime`/`gcTime` Infinity) and the
 * decode survives closing and reopening the dock.
 */
export function useClearance(meta: ClearanceLayerMeta | null, batchId?: string): UseClearanceResult {
  const [progress, setProgress] = useState<ClearanceFetchProgress | null>(null);
  const byproduct = useClearanceByproductUrl(batchId, meta?.byproducts?.json);
  const query = useQuery({
    queryKey: ['clearanceJson', meta?.byproducts?.json ?? null],
    enabled: !!byproduct.url,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<ClearanceJson> => {
      try {
        const buf = await fetchWithProgress(byproduct.url!, setProgress);
        const parsed = JSON.parse(new TextDecoder().decode(buf)) as ClearanceJson;
        if ((parsed.schemaVersion ?? 1) > CLEARANCE_SCHEMA_VERSION) throw new Error('SCHEMA_TOO_NEW');
        return parsed;
      } finally {
        // Always clear on the way out, success or failure, so a failed fetch
        // does not leave `phase: 'decoding'` stuck beside the error.
        setProgress(null);
      }
    },
  });
  const { refetch } = query;
  return {
    data: query.data ?? null,
    isLoading: byproduct.isLoading || query.isLoading || query.isFetching,
    error: byproduct.error ?? (query.error as Error) ?? null,
    progress,
    refetch: useCallback(() => void refetch(), [refetch]),
  };
}

/**
 * The per-station cross-section grid (`<stem>_station_clearance_section.u16`).
 *
 * Separate from `useClearance` on purpose: the grid is megabytes where the
 * JSON is kilobytes, and only the Profile tab reads it. Fetching it with the
 * chart data would put a multi-megabyte download in front of every dock open,
 * for a tab most readers never leave the default on.
 *
 * `enabled` is threaded through so the caller can hold the fetch until the tab
 * is actually shown; the query cache (`staleTime`/`gcTime: Infinity`, keyed by
 * filename) then keeps the decode across tab switches and dock close/open.
 *
 * Degrades rather than errors when the run predates the section byproduct:
 * `meta.byproducts.section` and `meta.sectionBins` are both optional, and with
 * either missing the query simply never runs.
 */
export function useClearanceSection(meta: ClearanceLayerMeta | null, batchId?: string, enabled = true) {
  const [progress, setProgress] = useState<ClearanceFetchProgress | null>(null);
  const filename = meta?.byproducts?.section ?? null;
  const bins = meta?.sectionBins ?? null;
  const byproduct = useClearanceByproductUrl(batchId, filename);
  const query = useQuery({
    queryKey: ['clearanceSection', filename],
    enabled: enabled && !!byproduct.url && !!bins,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<ClearanceSection> => {
      try {
        const buf = await fetchWithProgress(byproduct.url!, setProgress);
        return decodeClearanceSection(buf, bins!);
      } finally {
        setProgress(null);
      }
    },
  });
  const { refetch } = query;
  return {
    data: query.data ?? null,
    /** True only while something is actually in flight FOR this byproduct. */
    isLoading: !!filename && !!bins && (byproduct.isLoading || query.isLoading || query.isFetching),
    error: byproduct.error ?? (query.error as Error) ?? null,
    progress,
    /** No section in this run at all — the caller shows the static sketch. */
    unavailable: !filename || !bins,
    refetch: useCallback(() => void refetch(), [refetch]),
  };
}
